import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { IdpRucipGuard } from '@farm-portal/core/auth/idp-rucip.guard';
import { FullLayoutComponent } from '@farm-portal/core/layout';
import { NotFoundComponent } from '@farm-portal/notFound.component';
import { SiteTitle } from './app-site-titles.enum';
import { IdpAuthGuard } from './core/auth/idp-auth.guard';
import { IdpOrganizationGuard } from './core/auth/idp-organization.guard';
import { LoadAslUserDataComponent } from './core/layout/load-external-user-data/load-asl-user-data.component';
import { LoadUserDataComponent } from './core/layout/load-user-data/load-user-data.component';

const appRoutes: Routes = [
  {
    path: 'load-asl-data',
    component: LoadAslUserDataComponent
  },
  {
    path: 'auth',
    loadChildren: () => import('./modules/authorization/authorization.module').then(m => m.AuthorizationModule)
  },
  {
    path: 'load-user-data',
    component: LoadUserDataComponent,
    canActivate: [IdpAuthGuard]
  },
  {
    path: '',
    component: FullLayoutComponent,
    children: [
      {
        path: '',
        redirectTo: 'my-fields',
        pathMatch: 'full'
      },
      {
        path: 'contact',
        loadChildren: () => import('./modules/contact/contact.module').then(m => m.ContactModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.contact
        }
      },
      {
        path: 'toolbox',
        loadChildren: () => import('./modules/toolbox/toolbox.module').then(m => m.ToolboxModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.toolbox
        }
      },
      {
        path: 'farm',
        loadChildren: () => import('./modules/farm/farm.module').then(m => m.FarmModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.farm
        }
      },
      {
        path: 'carbon-assistant',
        loadChildren: () => import('./modules/carbon-assistant/carbon-assistant.module').then(m => m.CarbonAssistantModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.carbonAssistant
        }
      },
      {
        path: 'contracts',
        loadChildren: () => import('./modules/contracts/contracts.module').then(m => m.ContractsModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.contracts
        }
      },
      {
        path: 'my-fields',
        loadChildren: () => import('./modules/ground/ground.module').then(m => m.GroundModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.myFields
        }
      },
      {
        path: 'cultivation-assistant',
        loadChildren: () => import('./modules/cultivation-assistant/cultivation-assistant.module').then(m => m.CultivationAssistantModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.cultivationAssistant
        }
      },
      {
        path: 'buildings',
        loadChildren: () => import('./modules/buildings/buildings.module').then(m => m.BuildingsModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.buildings
        }
      },
      {
        path: 'livestock',
        loadChildren: () => import('./modules/livestock/livestock.module').then(m => m.LivestockModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.livestock
        }
      },
      {
        path: 'employees',
        loadChildren: () => import('./modules/employee/employee.module').then(m => m.EmployeeModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.employees
        }
      },
      {
        path: 'brigades',
        loadChildren: () => import('./modules/brigades/brigades.module').then(m => m.BrigadesModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: { title: SiteTitle.brigades }
      },
      {
        path: 'machines',
        loadChildren: () => import('./modules/machine/machine.module').then(m => m.MachineModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.machines
        }
      },
      {
        path: 'observations',
        loadChildren: () => import('./modules/observation/observation.module').then(m => m.ObservationModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.observations
        }
      },
      {
        path: 'rucip',
        loadChildren: () => import('./modules/rucip-observation/rucip.module').then(m => m.RucipModule),
        canLoad: [IdpAuthGuard, IdpRucipGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.rucip
        }
      },
      {
        path: 'fertilizer-stock-needs',
        loadChildren: () =>
          import('./modules/fertilizer-stock-needs/fertilizer-stock-needs.module').then(m => m.FertilizerStockNeedsModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.fertilizerStockNeeds
        }
      },
      {
        path: 'weatherstations',
        loadChildren: () => import('./modules/weather-station/weather-station.module').then(m => m.WeatherStationModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.weatherStations
        }
      },
      {
        path: `storageSensors`,
        loadChildren: () => import('./modules/storage-sensors/storage-sensors.module').then(m => m.StorageSensorsModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.storageSensors
        }
      },
      {
        path: `devices`,
        loadChildren: () => import('./modules/devices/devices.module').then(m => m.DevicesModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.devices
        }
      },
      {
        path: `irrigation`,
        loadChildren: () => import('./modules/irrigation/irrigation.module').then(m => m.IrrigationModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.irrigation
        }
      },
      {
        path: `opc`,
        loadChildren: () => import('./modules/opc/opc.module').then(m => m.OPCModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.opc
        }
      },
      {
        path: 'tunnels',
        loadChildren: () => import('./modules/tunnels/tunnels.module').then(m => m.TunnelsModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.tunnels
        }
      },
      {
        path: 'messages',
        loadChildren: () => import('./modules/message/message.module').then(m => m.MessageModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.messages
        }
      },
      {
        path: 'vehtel-monitoring',
        loadChildren: () => import('./modules/vehtel-monitoring/vehtel-monitoring.module').then(m => m.VehtelMonitoringModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.monitoring
        }
      },
      {
        path: 'report',
        loadChildren: () => import('./modules/report/report.module').then(m => m.ReportModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard]
      },
      {
        path: 'fertilizers-db',
        loadChildren: () => import('./modules/fertilizer-db/fertilizer-db.module').then(m => m.FertilizerDbModule),
        data: {
          title: SiteTitle.fertilizersDb
        }
      },
      {
        path: 'pesticides',
        loadChildren: () => import('./modules/pesticide/pesticide.module').then(m => m.PesticideModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.pesticides
        }
      },
      {
        path: 'documents',
        loadChildren: () => import('./modules/documents/documents.module').then(m => m.DocumentsModule),
        canActivate: [IdpAuthGuard, IdpOrganizationGuard],
        data: {
          title: SiteTitle.documents
        }
      },
      {
        path: 'invoices',
        loadChildren: () => import('./modules/invoices/invoices.module').then(m => m.InvoicesModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.invoices
        }
      },
      {
        path: 'ksef',
        loadChildren: () => import('./modules/ksef/ksef.module').then(m => m.KsefModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.ksef
        }
      },
      {
        path: 'partners',
        loadChildren: () => import('./modules/partners/partners.module').then(m => m.PartnersModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.partners
        }
      },
      {
        path: 'settings',
        loadChildren: () => import('./modules/settings/settings.module').then(m => m.SettingsModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.settings
        }
      },
      {
        path: 'stock-offers',
        loadChildren: () => import('./modules/stock-offer/stock-offer.module').then(m => m.StockOfferModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.stockOffers
        }
      },
      {
        path: 'suppliers',
        loadChildren: () => import('./modules/supplier/supplier.module').then(m => m.SupplierModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.suppliers
        }
      },
      {
        path: 'emission-calculations',
        loadChildren: () => import('./modules/emission-calculation/emission-calculation.module').then(m => m.EmissionCalculationModule),
        data: {
          title: SiteTitle.emissionCalculations
        }
      },
      {
        path: 'warehouses',
        loadChildren: () => import('./modules/warehouse/warehouse.module').then(m => m.WarehouseModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.warehousesProductStocks
        }
      },
      {
        path: 'soil-analysis',
        loadChildren: () => import('./modules/soil-analysis/soil-analysis.module').then(m => m.SoilAnalysisModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.soilAnalysis
        }
      },
      {
        path: 'maps',
        loadChildren: () => import('./modules/maps/maps.module').then(m => m.MapsModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.maps
        }
      },
      {
        path: 'satellite-images',
        loadChildren: () => import('./modules/satellite-images/satellite-images.module').then(m => m.SatelliteImagesModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.satelliteImages
        }
      },
      {
        path: 'treatments',
        loadChildren: () => import('./modules/treatments/treatments.module').then(m => m.TreatmentsModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.treatments
        }
      },
      {
        path: 'farm-tasks',
        loadChildren: () => import('./modules/farm-tasks/farm-tasks.module').then(m => m.FarmTasksModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.farmTasks
        }
      },
      {
        path: 'loyalty',
        loadChildren: () => import('./modules/loyalty-program/loyalty-program.module').then(m => m.LoyaltyProgramModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.loyaltyProgram
        }
      },
      {
        path: 'shop-offers',
        loadChildren: () => import('./modules/shop-offers/shop-offers.module').then(m => m.ShopOffersModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.loyaltyProgram
        }
      },
      {
        path: 'purchase-places',
        loadChildren: () => import('./modules/purchase-places/purchase-places.module').then(m => m.PurchasePlacesModule),
        canLoad: [IdpAuthGuard, IdpRucipGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.purchasePlaces
        }
      },
      {
        path: 'purchase-places-quotes',
        loadChildren: () =>
          import('./modules/purchase-places-quotes/purchase-places-quotes.module').then(m => m.PurchasePlacesQuotesModule),
        canLoad: [IdpAuthGuard], //, IdpRucipGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.purchasePlaces
        }
      },
      {
        path: 'passport',
        loadChildren: () => import('./modules/passport/passport.module').then(m => m.PassportModule),
        canLoad: [IdpAuthGuard],
        canActivateChild: [IdpOrganizationGuard],
        data: {
          title: SiteTitle.passport
        }
      }
    ],
    canActivateChild: [IdpOrganizationGuard]
  },
  {
    path: 'notFound',
    component: NotFoundComponent
  },
  {
    path: '**',
    component: NotFoundComponent
  }
];

@NgModule({
  imports: [
    RouterModule.forRoot(appRoutes, {
      onSameUrlNavigation: 'reload',
      scrollPositionRestoration: 'enabled'
    })
  ],
  exports: [RouterModule]
})
export class AppRoutingModule {}
